// src/components/modals/EditProductModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, Package, ChevronDown } from 'lucide-react'
import ProductService from '../../services/productService'
import CategoryService from '../../services/categoryService'
import UnitsService from '../../services/unitsService'
import { ProductModel } from '../../utils/productModel'

const EditProductModal = ({ isOpen, onClose, product, onProductUpdated, categories: categoriesProp = [], units: unitsProp = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    type: '',
    threshold: '',
    unit: '',
    cost: '',
    outletPrice: '',
    pickMePrice: '',
    uberPrice: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState(categoriesProp)
  const [units, setUnits] = useState(unitsProp)
  // Removed dropdownsLoading state (no longer needed)

  // Get product types from service
  const productTypes = ProductService.getProductTypes()

  // Initialize form data when modal opens or product changes
  useEffect(() => {
    if (isOpen && product) {
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        category: product.category || '',
        type: product.type || '',
        threshold: product.threshold || '',
        unit: product.unit || '',
        cost: product.cost || '',
        outletPrice: product.outletPrice || '',
        pickMePrice: product.pickMePrice || '',
        uberPrice: product.uberPrice || '',
        isActive: product.isActive !== false
      })
      setErrors({})
      setCategories(categoriesProp)
      setUnits(unitsProp)
    }
  }, [isOpen, product, categoriesProp, unitsProp])

  // Remove fetchDropdownData, use props instead

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Auto uppercase SKU
    if (name === 'sku') {
      setFormData(prev => ({
        ...prev,
        sku: value.toUpperCase()
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate form
  const validateForm = () => {
    const validation = ProductModel.validateProduct(formData)
    
    // Custom validation for recipe_based products
    if (formData.type === 'recipe_based') {
      // Remove unit and threshold validation for recipe_based products
      delete validation.errors.unit
      delete validation.errors.threshold
    }
    
    setErrors(validation.errors)
    return Object.keys(validation.errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      // Prepare data for API - only send changed fields
      const updateData = {}
      
      // Normalize strings for comparison to avoid whitespace issues
      const trimmedName = formData.name.trim()
      const trimmedSku = formData.sku.trim().toUpperCase()
      const originalName = (product.name || '').trim()
      const originalSku = (product.sku || '').trim().toUpperCase()
      
      if (trimmedName !== originalName) {
        updateData.name = trimmedName
      }
      
      if (trimmedSku !== originalSku) {
        updateData.sku = trimmedSku
      }
      
      if (formData.category !== product.category) {
        updateData.category = formData.category
      }
      
      if (formData.type !== product.type) {
        updateData.type = formData.type
      }
      
      // Only include unit and threshold for product type, and only if changed
      if (formData.type === 'product') {
        const currentThreshold = parseInt(formData.threshold) || 0
        const originalThreshold = parseInt(product.threshold) || 0
        
        if (currentThreshold !== originalThreshold) {
          updateData.threshold = currentThreshold
        }
        
        // Note: Unit is typically not changeable after creation as per existing code
        // but including for completeness - API should handle restrictions
        if (formData.unit !== product.unit) {
          updateData.unit = formData.unit
        }
      }
      
      const currentCost = parseFloat(formData.cost) || 0
      const originalCost = parseFloat(product.cost) || 0
      if (Math.abs(currentCost - originalCost) > 0.001) {
        updateData.cost = currentCost
      }
      
      const currentOutletPrice = parseFloat(formData.outletPrice) || 0
      const originalOutletPrice = parseFloat(product.outletPrice) || 0
      if (Math.abs(currentOutletPrice - originalOutletPrice) > 0.001) {
        updateData.outletPrice = currentOutletPrice
      }
      
      const currentPickMePrice = parseFloat(formData.pickMePrice) || 0
      const originalPickMePrice = parseFloat(product.pickMePrice) || 0
      if (Math.abs(currentPickMePrice - originalPickMePrice) > 0.001) {
        updateData.pickMePrice = currentPickMePrice
      }
      
      const currentUberPrice = parseFloat(formData.uberPrice) || 0
      const originalUberPrice = parseFloat(product.uberPrice) || 0
      if (Math.abs(currentUberPrice - originalUberPrice) > 0.001) {
        updateData.uberPrice = currentUberPrice
      }
      
      if (formData.isActive !== product.isActive) {
        updateData.isActive = formData.isActive
      }
      
      // Only proceed if there are changes
      if (Object.keys(updateData).length === 0) {
        console.log('ℹ️ No changes detected, closing modal')
        handleClose()
        return
      }
      
      console.log('📤 Sending update with changes:', updateData)
      
      const response = await ProductService.updateProduct(product._id, updateData)
      
      if (response.success) {
        const updatedProduct = response.data?.product || { ...product, ...updateData }
        onProductUpdated(updatedProduct)
        handleClose()
      } else {
        setErrors({ general: response.message || 'Failed to update product' })
      }
    } catch (error) {
      console.error('❌ Error updating product:', error)
      
      // Enhanced error handling for specific error messages
      let errorMessage = 'Failed to update product. Please try again.'
      
      if (error.message.includes('already exists')) {
        if (error.message.includes('name')) {
          errorMessage = 'A product with this name already exists. Please choose a different name.'
        } else if (error.message.includes('sku')) {
          errorMessage = 'A product with this SKU already exists. Please choose a different SKU.'
        } else {
          errorMessage = 'This product information already exists. Please check your input.'
        }
      } else if (error.message.includes('not found')) {
        errorMessage = 'Product not found. It may have been deleted by another user.'
      } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your input and try again.'
      }
      
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setFormData({
      name: '',
      sku: '',
      category: '',
      type: '',
      threshold: '',
      unit: '',
      cost: '',
      outletPrice: '',
      pickMePrice: '',
      uberPrice: '',
      isActive: true
    })
    setErrors({})
    onClose()
  }

  // Check if current product type is recipe_based
  const isRecipeBased = formData.type === 'recipe_based'

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {/* <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Package className="h-5 w-5 text-orange-600" />
                </div> */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Edit Product</h3>
                </div>
              </div>
              <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                </div>
              )}

              {/* Row 1: Name & SKU */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Espresso Standard"
                    disabled={loading}
                    className={errors.name ? 'border-red-300' : ''}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU *
                  </label>
                  <Input
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="e.g. ES1200"
                    disabled={loading}
                    className={errors.sku ? 'border-red-300' : ''}
                  />
                  {errors.sku && (
                    <p className="text-red-500 text-sm mt-1">{errors.sku}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Category & Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={loading}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.category ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type *
                  </label>
                  <div className="relative">
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled={loading}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.type ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Select Product Type</option>
                      {productTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                  )}
                </div>
              </div>

              {/* Row 3: Threshold & Unit - Conditional Based on Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Threshold {!isRecipeBased && '*'}
                  </label>
                  <Input
                    type="number"
                    name="threshold"
                    value={formData.threshold}
                    onChange={handleInputChange}
                    placeholder="Enter threshold (e.g., 10)"
                    min="0"
                    disabled={loading || isRecipeBased}
                    readOnly={isRecipeBased}
                    className={`${errors.threshold ? 'border-red-300' : ''} ${isRecipeBased ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {isRecipeBased && (
                    <p className="text-xs text-gray-500 mt-1">Not applicable for recipe-based products</p>
                  )}
                  {errors.threshold && (
                    <p className="text-red-500 text-sm mt-1">{errors.threshold}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit {!isRecipeBased && '*'}
                  </label>
                  <div className="relative">
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      disabled={true} // Always disabled in edit mode as per existing logic OR isRecipeBased
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.unit ? 'border-red-300' : 'border-gray-200'
                      } bg-gray-100`}
                    >
                      <option value="">Select unit</option>
                      {units.map(unit => (
                        <option key={unit._id} value={unit._id}>
                          {unit.name} ({unit.abbreviation})
                        </option>
                      ))}
                      {/* Show current unit even if not in the active units list */}
                      {formData.unit && !units.find(u => u._id === formData.unit) && (
                        <option value={formData.unit}>
                          {product?.unitDetails?.name ? 
                            `${product.unitDetails.name} (${product.unitDetails.abbreviation}) - Inactive` : 
                            'Current Unit - Details not available'
                          }
                        </option>
                      )}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {isRecipeBased ? (
                    <p className="text-xs text-gray-500 mt-1">Not applicable for recipe-based products</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Unit cannot be changed after creation</p>
                  )}
                  {errors.unit && (
                    <p className="text-red-500 text-sm mt-1">{errors.unit}</p>
                  )}
                </div>
              </div>

              {/* Row 4: Cost & Outlet Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    placeholder="Enter cost (e.g., 120.50)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.cost ? 'border-red-300' : ''}
                  />
                  {errors.cost && (
                    <p className="text-red-500 text-sm mt-1">{errors.cost}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outlet Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="outletPrice"
                    value={formData.outletPrice}
                    onChange={handleInputChange}
                    placeholder="Enter outlet price (e.g., 200.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.outletPrice ? 'border-red-300' : ''}
                  />
                  {errors.outletPrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.outletPrice}</p>
                  )}
                </div>
              </div>

              {/* Row 5: PickMe Price & Uber Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PickMe Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="pickMePrice"
                    value={formData.pickMePrice}
                    onChange={handleInputChange}
                    placeholder="Enter PickMe price (e.g., 220.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.pickMePrice ? 'border-red-300' : ''}
                  />
                  {errors.pickMePrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.pickMePrice}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uber Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="uberPrice"
                    value={formData.uberPrice}
                    onChange={handleInputChange}
                    placeholder="Enter Uber price (e.g., 225.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.uberPrice ? 'border-red-300' : ''}
                  />
                  {errors.uberPrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.uberPrice}</p>
                  )}
                </div>
              </div>

              {/* Status Toggle */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <button
                  type="button"
                  onClick={handleStatusToggle}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-6">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-black hover:bg-gray-800 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Product'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditProductModal